import { useState } from 'react';
import { Link, useLocation } from 'react-router';
import type { Twin, QuizItem } from '../../types';
import { Card, CardHeader } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { formatDate } from '../../utils/helpers';
import { api } from '../../services/api';
import { useToast } from '../ui/Toast';
import { TwinProfileDetails } from '../twins/TwinProfileDetails';
import { Bot, Lock, Unlock, ChevronDown, ChevronUp, CheckCircle, Circle, Trash2, ExternalLink } from 'lucide-react';

interface TwinListProps {
  userId: string;
  twins: Twin[];
  onTwinsChanged: () => void | Promise<void>;
}

function TwinFormDetails({ twin }: { twin: Twin }) {
  const fields: { label: string; value?: string }[] = [
    { label: 'Role', value: twin.role },
    { label: 'Target Audience', value: twin.target },
    { label: 'Purpose', value: twin.purpose },
    { label: 'Requirements', value: twin.requirements },
    { label: 'Context', value: twin.context },
  ].filter((f) => f.value && f.value.trim() !== '');

  if (fields.length === 0) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">
        Twin Creation Form
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        {fields.map((f) => (
          <div key={f.label} className="bg-[#1c1c2a] rounded-lg px-3 py-2">
            <p className="text-[10px] text-[#64748b] uppercase tracking-wide">{f.label}</p>
            <p className="text-xs text-[#f1f5f9] mt-0.5 leading-relaxed whitespace-pre-wrap break-words">
              {f.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

function QuizAnswers({ quiz }: { quiz: QuizItem[] }) {
  const answered = quiz.filter((q) => q.answer && q.answer.trim() !== '');
  if (answered.length === 0) return null;

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider">
        Quiz Answers ({answered.length})
      </p>
      {answered.map((item, i) => (
        <div key={i} className="space-y-1">
          <p className="text-xs text-[#94a3b8] leading-snug">
            <span className="text-[#64748b] font-medium">Q{i + 1}. </span>
            {item.question}
          </p>
          {item.type === 'mcq' && item.options ? (
            <div className="grid grid-cols-2 gap-1 pl-3">
              {(Object.entries(item.options) as [string, string][]).map(([key, text]) => {
                const isSelected = item.answer.trim().toLowerCase() === key.toLowerCase()
                  || item.answer.trim().toLowerCase() === text.trim().toLowerCase();
                return (
                  <div
                    key={key}
                    className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-lg ${
                      isSelected
                        ? 'bg-[#1e3a5f] border border-[#3b82f6] text-[#93c5fd]'
                        : 'text-[#64748b]'
                    }`}
                  >
                    {isSelected
                      ? <CheckCircle className="w-3 h-3 shrink-0 text-[#3b82f6]" />
                      : <Circle className="w-3 h-3 shrink-0" />}
                    <span className="font-medium uppercase mr-0.5">{key}.</span>
                    {text}
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-xs text-[#f1f5f9] bg-[#1c1c2a] rounded-lg px-3 py-1.5 ml-3">
              {item.answer}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}

function TwinCard({
  twin,
  onRequestDelete,
}: {
  twin: Twin;
  onRequestDelete: (twin: Twin) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const hasQuiz = twin.quiz && twin.quiz.length > 0;
  const hasFormDetails = Boolean(
    twin.role || twin.target || twin.purpose || twin.requirements || twin.context
  );
  const hasProfileDetails = Boolean(twin.profileDetails);
  const hasDetails = hasQuiz || hasFormDetails || hasProfileDetails;

  return (
    <div className="bg-[#12121a] rounded-lg border border-[#2a2a3a] overflow-hidden">
      {/* Header row */}
      <div className="p-4">
        <div className="flex items-start justify-between">
          <Link
            to={`/twins/${twin.id}`}
            state={{ from: location.pathname }}
            className="group flex items-center gap-3 min-w-0"
            title="Open twin details"
          >
            <div className="w-10 h-10 bg-[#1a1a24] border border-[#2a2a3a] rounded-lg flex items-center justify-center shrink-0 group-hover:border-[#3b82f6] transition-colors">
              <Bot className="w-5 h-5 text-[#3b82f6]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-[#f8fafc] group-hover:text-[#60a5fa] group-hover:underline transition-colors">
                  {twin.name}
                </h4>
                {twin.locked
                  ? <Lock className="w-3.5 h-3.5 text-[#f59e0b]" />
                  : <Unlock className="w-3.5 h-3.5 text-[#22c55e]" />}
                <ExternalLink className="w-3.5 h-3.5 text-[#64748b] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="text-sm text-[#94a3b8]">{twin.purpose || 'No purpose set'}</p>
            </div>
          </Link>

          <div className="flex items-center gap-2 shrink-0">
            <Badge variant={twin.active ? 'success' : 'default'}>
              {twin.active ? 'Active' : 'Inactive'}
            </Badge>
            {hasDetails && (
              <button
                type="button"
                onClick={() => setExpanded((e) => !e)}
                className="flex items-center gap-1 text-xs text-[#64748b] hover:text-[#f1f5f9] transition-colors px-2 py-1 rounded-lg hover:bg-[#1c1c2a]"
                title={expanded ? 'Hide details' : 'Show details'}
              >
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                Details
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRequestDelete(twin);
              }}
              className="flex items-center gap-1 text-xs text-[#f87171] hover:text-[#fca5a5] transition-colors px-2 py-1 rounded-lg hover:bg-[#3f1d1d]/60 border border-transparent hover:border-[#7f1d1d]/80"
              title="Delete twin"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Delete
            </button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-[#2a2a3a]">
          <div>
            <p className="text-xs text-[#64748b] uppercase tracking-wide">Type</p>
            <p className="text-sm text-[#f8fafc] mt-0.5 capitalize">{twin.type}</p>
          </div>
          <div>
            <p className="text-xs text-[#64748b] uppercase tracking-wide">Understanding</p>
            <p className="text-sm text-[#f8fafc] mt-0.5">{twin.understanding}%</p>
          </div>
          <div>
            <p className="text-xs text-[#64748b] uppercase tracking-wide">Created</p>
            <p className="text-sm text-[#f8fafc] mt-0.5">{formatDate(twin.createdAt)}</p>
          </div>
        </div>

        {/* Candidate/hiring profile + creation form + Quiz Q&A */}
        {expanded && hasDetails && (
          <div className="mt-3 pt-3 border-t border-[#2a2a3a] space-y-4">
            {hasProfileDetails && <TwinProfileDetails type={twin.type} details={twin.profileDetails} />}
            <TwinFormDetails twin={twin} />
            {hasQuiz && <QuizAnswers quiz={twin.quiz!} />}
          </div>
        )}
      </div>

      {/* Details summary bar (when collapsed) */}
      {!expanded && hasDetails && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-center gap-2 py-2 border-t border-[#2a2a3a] text-xs text-[#64748b] hover:text-[#94a3b8] hover:bg-[#1c1c2a] transition-colors"
        >
          <ChevronDown className="w-3.5 h-3.5" />
          {hasQuiz
            ? `${twin.quiz!.filter((q) => q.answer?.trim()).length} quiz answer${twin.quiz!.filter((q) => q.answer?.trim()).length !== 1 ? 's' : ''} & creation form`
            : 'Twin creation form'}{' '}
          - click to view
        </button>
      )}
    </div>
  );
}

export function TwinList({ userId, twins, onTwinsChanged }: TwinListProps) {
  const { toast } = useToast();
  const [twinPendingDelete, setTwinPendingDelete] = useState<Twin | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!twinPendingDelete) return;
    setDeleting(true);
    try {
      const res = await api.deleteUserTwin(userId, twinPendingDelete.id);
      const extra =
        res.deletedUserChats > 0 || res.deletedTwinChats > 0
          ? ` (removed ${res.deletedUserChats} recruiter chats, ${res.deletedTwinChats} twin transcripts)`
          : '';
      toast((res.message || 'Twin deleted') + extra, 'success');
      setTwinPendingDelete(null);
      await onTwinsChanged();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete twin', 'error');
    } finally {
      setDeleting(false);
    }
  };

  if (twins.length === 0) {
    return (
      <Card>
        <CardHeader title="User Twins" />
        <div className="text-center py-8 text-[#94a3b8]">No twins created yet</div>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader title={`User Twins (${twins.length})`} />
        <div className="space-y-3 mt-4">
          {twins.map((twin) => (
            <TwinCard key={twin.id} twin={twin} onRequestDelete={setTwinPendingDelete} />
          ))}
        </div>
      </Card>

      <Modal
        isOpen={Boolean(twinPendingDelete)}
        onClose={() => !deleting && setTwinPendingDelete(null)}
        title="Delete twin"
        size="sm"
        closeOnBackdropClick={!deleting}
        allowDismiss={!deleting}
        footer={
          <>
            <Button variant="ghost" onClick={() => setTwinPendingDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void handleConfirmDelete()} loading={deleting}>
              Delete twin
            </Button>
          </>
        }
      >
        {twinPendingDelete ? (
          <div className="space-y-3">
            <p className="text-[#94a3b8]">
              This permanently removes{' '}
              <span className="text-[#f8fafc] font-medium">{twinPendingDelete.name}</span> and all
              related match chats and twin-to-twin transcripts. This cannot be undone.
            </p>
            <p className="text-[#64748b] text-sm">
              Twin ID: <span className="font-mono text-[#94a3b8]">{twinPendingDelete.id}</span>
            </p>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
